import React from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BasePersonTimelineRecordLayout from 'domain/shared/timeline/person/PersonTimelineRecord/BasePersonTimelineRecordLayout';
import { useShowContext } from 'framework/show';
import { DATE_FORMAT } from 'framework/utils';
import {
  personTimelineActionNameSx,
  personTimelineDateSx,
  personTimelineHeaderSx,
} from 'domain/shared/timeline/person/PersonTimelineRecord/personTimelineRecordSx';
import { PROJECT_SUMMARY_REVIEW_TIMELINE_ICONS, PROJECT_TIMELINE_MESSAGE_CONTEXT } from './constants';
import { ITimelineItem, SummaryReviewPayload } from './types';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { ShowDate } from 'framework/ui/ui-builder';
import { useNewProjectUrl, useProjectDataContext } from 'domain/shared/newProject';

const ProjectSummaryReviewTimelineRecordHeader = () => {
  const { data: entity } = useShowContext<ITimelineItem<SummaryReviewPayload>>();

  return (
    <Box sx={personTimelineHeaderSx}>
      <Box sx={personTimelineActionNameSx}>Project review finalized</Box>
      <Box sx={personTimelineDateSx}>
        <ShowDate dateUTC={entity?.createdAt} format={DATE_FORMAT.FULL} />
      </Box>
    </Box>
  );
};

const ProjectSummaryReviewTimelineRecordLayout = (props: Record<string, unknown>) => {
  const { data: entity } = useShowContext<ITimelineItem<SummaryReviewPayload>>();
  const { data: project } = useProjectDataContext();
  const { getSummaryReviewUrl } = useNewProjectUrl();
  const payload = entity?.payload || ({} as SummaryReviewPayload);
  const { reviewId, startDate, endDate, reviewer } = payload;

  const renderSummaryReviewFields = () => (
    <Stack gap={1}>
      {(startDate || endDate) && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1" color="textSecondary">
            Review period:
          </Typography>
          <Typography>
            {startDate && <ShowDate dateUTC={startDate} format={DATE_FORMAT.API_ONLY_DATE} />}
            {' – '}
            {endDate && <ShowDate dateUTC={endDate} format={DATE_FORMAT.API_ONLY_DATE} />}
          </Typography>
        </Stack>
      )}

      {reviewer && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1" color="textSecondary">
            Reviewer:
          </Typography>
          <EmployeeLinkFieldWithNameLoading value={reviewer} />
        </Stack>
      )}

      {reviewId && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Link component={RouterLink} to={getSummaryReviewUrl(project.id, reviewId)} underline="hover">
            View review
          </Link>
        </Stack>
      )}
    </Stack>
  );

  return (
    <BasePersonTimelineRecordLayout
      actionName=""
      timelineType="SummaryReview"
      systemNoteType="SummaryReview"
      timelineIcons={PROJECT_SUMMARY_REVIEW_TIMELINE_ICONS}
      timelineHeader={ProjectSummaryReviewTimelineRecordHeader}
      fields={renderSummaryReviewFields()}
      isCandidateService={false}
      timelineFooter={() => null}
      messageContext={PROJECT_TIMELINE_MESSAGE_CONTEXT}
      {...props}
    />
  );
};

export default ProjectSummaryReviewTimelineRecordLayout;
